<template>
  <div v-if="isNotFound" class="notfound__container">
    <not-found></not-found>
  </div>
  <section v-else-if="notice" class="container">
    <div class="notice__header row">
      <img
        class="notice__header__img"
        src="https://reality-notice.wrightflyer.net/bbca65fa40a1f111ba0216b1dc1fbb5fd3784816.png"
      />
      <div class="notice__header__right">
        <div class="notice__header__detail">
          <div class="notice__header__contributor-name">
            {{ $t('contributorName') }}
          </div>
        </div>
        <div class="notice__header__title">{{ title }}</div>
      </div>
    </div>
    <div class="notice__body row">
      <i18n v-if="category" path="penaltyNoticeBodyWithCategory" tag="div">
        <template #br><br /></template>
        <template #termsOfUse>
          <a :href="$t('termsOfUseUrl')">{{ $t('termsOfUse') }}</a>
        </template>
        <template v-if="place" #placeMessage>
          {{ $t('penaltyNoticePlace', { place }) }}
        </template>
        <template #category>{{ category }}</template>
        <template #reason>{{ reason }}</template>
        <template #message1>
          <template v-if="isWarning">
            {{ $t('penaltyNoticeMessage1Warning') }}
          </template>
          <template v-if="isSuspend">
            {{ $t('penaltyNoticeMessage1Suspend') }}
          </template>
          <template v-if="isStop">
            {{ $t('penaltyNoticeMessage1Stop') }}
          </template>
        </template>
        <template v-if="!isStop" #message2>
          <br />
          <template v-if="isWarning">
            {{ $t('penaltyNoticeMessage2Warning') }}
          </template>
          <template v-if="isSuspend">
            {{ $t('penaltyNoticeMessage2Suspend') }}
          </template>
          <br />
        </template>
        <template v-if="!isStop" #message3>
          <br />
          <i18n path="penaltyNoticeMessage3">
            <template #br><br /></template>
            <template #termsOfUse>
              <a :href="$t('termsOfUseUrl')">{{ $t('termsOfUse') }}</a>
            </template>
          </i18n>
        </template>
      </i18n>

      <i18n v-else path="penaltyNoticeBody" tag="div">
        <template #br><br /></template>
        <template #termsOfUse>
          <a :href="$t('termsOfUseUrl')">{{ $t('termsOfUse') }}</a>
        </template>
        <template #message1>
          <template v-if="isWarning">
            {{ $t('penaltyNoticeMessage1Warning') }}
          </template>
          <template v-if="isSuspend">
            {{ $t('penaltyNoticeMessage1Suspend') }}
          </template>
          <template v-if="isStop">
            {{ $t('penaltyNoticeMessage1Stop') }}
          </template>
        </template>
        <template #reason>{{ reason }}</template>
        <template #message2>
          <template v-if="isWarning">
            {{ $t('penaltyNoticeMessage2Warning') }}
            <br />
            <br />
          </template>
          <template v-else>
            {{ $t('penaltyNoticeMessage2NotWarning') }}
          </template>
        </template>
        <template v-if="!isStop" #message3>
          <br />
          <i18n path="penaltyNoticeMessage3">
            <template #br><br /></template>
            <template #termsOfUse>
              <a :href="$t('termsOfUseUrl')">{{ $t('termsOfUse') }}</a>
            </template>
          </i18n>
        </template>
      </i18n>
    </div>
    <div class="footer">
      <div id="copyright">© REALITY</div>
    </div>
  </section>
</template>

<script lang="ts">
import { Component, Mixins } from 'vue-property-decorator'
import { showGenericNetworkError } from '@/utils/handle_error'
import NotFound from '@/components/Error/NotFound.vue'
import i18n from '@/i18n'
import { LanguageQueryHandler } from '@/mixins/LanguageQuery'
import {
  PenaltyNotice,
  penaltyTypes,
  penaltyReasons,
  penaltyPlaces,
  penaltyCategories,
} from '@/models/PenaltyNotice'
import { api } from '@/api/appGatewayServerApi'
import { ApiError } from '@/api/error'

@Component({
  components: {
    NotFound,
  },
})
export default class NoticePenalty extends Mixins(LanguageQueryHandler) {
  notice: PenaltyNotice | null = null
  isNotFound: boolean = false

  get title() {
    const titles = {
      [penaltyTypes.Warning]: i18n.t('penaltyNoticeTitleWarning'),
      [penaltyTypes.Suspend]: i18n.t('penaltyNoticeTitleSuspend'),
      [penaltyTypes.Stop]: i18n.t('penaltyNoticeTitleStop'),
    }
    return titles[this.notice!.penaltyType]
  }
  get reason() {
    const penaltyReasonNames = {
      [penaltyReasons.AntiSocial]: i18n.t('penaltyNoticeReason1'),
      [penaltyReasons.Obscenity]: i18n.t('penaltyNoticeReason2'),
      [penaltyReasons.Commercial]: i18n.t('penaltyNoticeReason3'),
      [penaltyReasons.PersonalInfo]: i18n.t('penaltyNoticeReason4'),
      [penaltyReasons.Official]: i18n.t('penaltyNoticeReason5'),
      [penaltyReasons.Misconduct]: i18n.t('penaltyNoticeReason6'),
      [penaltyReasons.Other]: i18n.t('penaltyNoticeReason7'),
    }
    return penaltyReasonNames[this.notice!.penaltyReason]
  }
  get place() {
    const penaltyPlaceNames = {
      [penaltyPlaces.MediaComment]: i18n.t('penaltyPlaceMediaComment'),
      [penaltyPlaces.MediaContent]: i18n.t('penaltyPlaceMediaContent'),
      [penaltyPlaces.MediaTitle]: i18n.t('penaltyPlaceMediaTitle'),
      [penaltyPlaces.Profile]: i18n.t('penaltyPlaceProfile'),
      [penaltyPlaces.Other]: '', // other はユーザには表示しない
      [penaltyPlaces.Chat]: i18n.t('penaltyPlaceChat'),
      [penaltyPlaces.VideoChat]: i18n.t('penaltyPlaceVideoChat'),
      [penaltyPlaces.ObsStreaming]: i18n.t('penaltyPlaceObsStreaming'),
      [penaltyPlaces.FeedPost]: i18n.t('penaltyPlaceFeedPost'),
      [penaltyPlaces.FeedComment]: i18n.t('penaltyPlaceFeedComment'),
    } as const
    return penaltyPlaceNames[this.notice!.penaltyPlace]
  }
  get category() {
    const penaltyCategoryNames = {
      [penaltyCategories.Illegal]: i18n.t('penaltyCategoryIllegal'),
      [penaltyCategories.Suicide]: i18n.t('penaltyCategorySuicide'),
      [penaltyCategories.Dating]: i18n.t('penaltyCategoryDating'),
      [penaltyCategories.PersonalInfo]: i18n.t('penaltyCategoryPersonalInfo'),
      [penaltyCategories.Calumny]: i18n.t('penaltyCategoryCalumny'),
      [penaltyCategories.Violence]: i18n.t('penaltyCategoryViolence'),
      [penaltyCategories.Spoofing]: i18n.t('penaltyCategorySpoofing'),
      [penaltyCategories.Obscenity]: i18n.t('penaltyCategoryObscenity'),
      [penaltyCategories.Commercial]: i18n.t('penaltyCategoryCommercial'),
      [penaltyCategories.Url]: i18n.t('penaltyCategoryUrl'),
      [penaltyCategories.Rights]: i18n.t('penaltyCategoryRights'),
      [penaltyCategories.Troll]: i18n.t('penaltyCategoryTroll'),
      [penaltyCategories.Misconduct]: i18n.t('penaltyCategoryMisconduct'),
      [penaltyCategories.Other]: i18n.t('penaltyCategoryOther'),
      [penaltyCategories.PointFraud]: i18n.t('penaltyCategoryPointFraud'),
    } as const
    return penaltyCategoryNames[this.notice!.category]
  }

  get isWarning() {
    return this.notice!.penaltyType === penaltyTypes.Warning
  }
  get isSuspend() {
    return this.notice!.penaltyType === penaltyTypes.Suspend
  }
  get isStop() {
    return this.notice!.penaltyType === penaltyTypes.Stop
  }

  async created() {
    await this.fetchPenaltyNotice()
  }

  async fetchPenaltyNotice() {
    try {
      const id = Number(this.$route.params.id)
      const response = await api().notice.penalty.getValid({ id })
      this.notice = response.penaltyNotice
    } catch (e) {
      if (e instanceof ApiError) {
        // 下記ステータスコードの場合は Not Found 表示にする
        // 401: 未ログイン
        // 403: お知らせ対象でないユーザ
        // 404: 指定IDのお知らせが存在しない
        if ([401, 403, 404].includes(e.status)) {
          this.isNotFound = true
          return
        }
      }
      showGenericNetworkError()
    }
  }
}
</script>

<style scoped>
.footer {
  margin-top: 1em;
  height: 6em;
}

.notfound__container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
}

#copyright {
  text-align: center;
}

a {
  color: #007bff;
  text-decoration: none;
  background-color: transparent;
  -webkit-text-decoration-skip: objects;
}

a:hover {
  color: #0056b3;
  text-decoration: underline;
}

.row {
  display: -ms-flexbox;
  display: flex;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
  margin-right: -15px;
  margin-left: -15px;
}

.container {
  width: 100%;
  padding-right: 15px;
  padding-left: 15px;
  margin-right: auto;
  margin-left: auto;
  font-size: 14px;
  color: rgb(38, 38, 38);
}

@media (min-width: 576px) {
  .container {
    max-width: 540px;
  }
}

@media (min-width: 768px) {
  .container {
    max-width: 720px;
  }
}

@media (min-width: 992px) {
  .container {
    max-width: 960px;
  }
}

@media (min-width: 1200px) {
  .container {
    max-width: 1140px;
  }
}

.notice__header {
  align-items: center;
  padding: 12px 20px;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-wrap: nowrap;
  flex-wrap: nowrap;
  border-bottom: 1px solid #e8e8e8;
}

.notice__header__img {
  display: inline-block;
  height: 44px;
  border-radius: 22px;
  vertical-align: top;
  margin-right: 8px;
}

.notice__header__right {
  display: inline-block;
}

.notice__header__detail {
  color: grey;
  font-size: 12px;
  margin-top: 4px;
}

.notice__header__contributor-name {
  display: inline;
  margin-right: 20px;
}

.notice__header__date {
  display: inline;
}

.notice__header__title {
  display: inline-block;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notice__body {
  padding: 18px 20px;
  border: none;
  line-height: 1.6;
  text-align: justify;
}

.notice__img {
  width: 100%;
}

@media screen and (min-width: 768px) {
  .notice__header__right {
    max-width: 600px;
  }
}
</style>
